import clsx from 'clsx';
import { useAtom } from 'jotai';
import { AnimatePresence, motion } from 'motion/react';
import { type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { Filters } from '@/components/filters';
import { SmartList } from '@/components/smart-list';
import { MiniActionBar } from '@/components/mini-action-bar';
import { Search } from '@flintmint/web-shared/components/search';
import { ListItem, type ListItemProps } from '@/components/list-item';
import { SortMenu } from '@flintmint/web-shared/components/sort-menu';

import { useSearch } from '@/hooks/use-search';
import { useItemActions } from '@/hooks/use-item-actions';
import { useSearchSort } from '@/hooks/use-search-sort';
import { useWorkspaceSort } from '@/hooks/use-workspace-sort';
import { useAgentRunUiState } from '@/hooks/llm/use-llm-agent';
import type { SidebarAppliedFilters } from '@/hooks/use-sidebar-search-and-filters';

import { multiSelectionsAtom } from '@/state';
import { conditionalSpread } from '@flintmint/web-shared/utils/helper-utils';
import { getRangeSlice, toggleObject } from '@/utils/helper-utils';

import type { DocumentWithId } from '@/constants/types';
import type { SearchableDocument } from '@/services/search-service/orama-schema';
import type { MenuProps } from '@flintmint/web-shared/components/menu';
import type { SearchQuery } from '@/hooks/use-search';

import styles from './docu-list.module.scss';

export const DocuList = (props: DocuListProps) => {
  const {
    mode,
    menu,
    noData,
    actions,
    filters,
    showDate,
    selections,
    searchValue,
    listHeader,
    searchQuery,
    onDoubleClick,
    bottomActions,
    onSearchChange,
    appliedFilters,
    showDescription,
    searchableTypes,
    defaultSize = 82,
    activeDocumentId,
    hasFilters = true,
    onAppliedFiltersChange,
    appearance = 'default',
    ...rest
  } = props;

  const [anchorId, setAnchor] = useState<string | null>(null);
  const [lastShiftRangeIds, setLastShiftRangeIds] = useState<string[]>([]);
  const [isLastItemVisible, setIsLastItemVisible] = useState(false);

  const { includedTagIds: tagIds, excludedTagIds, date } = appliedFilters;

  const [multiSelections, setMultiSelections] = useAtom(multiSelectionsAtom);

  const { openDocument } = useItemActions();
  const { sortParams, onSort } = useWorkspaceSort();
  const { isSearching, menuSort, querySort, setSearchSort } = useSearchSort(searchValue, sortParams);

  const multiSelectionOn = Array.isArray(multiSelections);
  const [dateStart, dateEnd] = date?.range ?? [null, null];

  const query: SearchQuery = {
    term: searchValue,
    ...searchQuery,
    isUserSearch: true,
    where: {
      types: searchableTypes,
      ...conditionalSpread({ tagIds }, !!tagIds.length),
      ...conditionalSpread({ excludeTagIds: excludedTagIds }, !!excludedTagIds.length),
      ...conditionalSpread({ updatedSince: dateStart }, !!dateStart),
      ...conditionalSpread({ updatedTo: dateEnd }, !!dateEnd),
      ...(searchQuery?.where || {}),
    },
    sort: querySort,
  };

  const { data, isLoading } = useSearch(query);

  const resetKey = useMemo(() => JSON.stringify(query), [query]);
  const dataSearchableDocuments = useMemo(() => data.map((item) => item.document), [data]);

  const clearSelections = () => {
    setMultiSelections(null);
    setAnchor(null);
    setLastShiftRangeIds([]);
  };

  useEffect(() => {
    return () => clearSelections();
  }, []);

  const handleShiftClick = useCallback(
    (selectedDocument: SearchableDocument) => {
      const anchorInList = anchorId && dataSearchableDocuments.some((d) => d.id === anchorId);

      if (!anchorInList) {
        setMultiSelections([selectedDocument]);
        setAnchor(selectedDocument.id);
        setLastShiftRangeIds([selectedDocument.id]);
        return;
      }

      const range = getRangeSlice(dataSearchableDocuments, anchorId!, selectedDocument.id);
      if (!range) return;

      setMultiSelections((prev) => {
        const filtered = (prev ?? []).filter((d) => !lastShiftRangeIds.includes(d.id));
        const rangeIds = new Set(range.map((d) => d.id));
        const keep = filtered.filter((d) => !rangeIds.has(d.id));
        return [...keep, ...range];
      });
      setLastShiftRangeIds(range.map((d) => d.id));
    },
    [anchorId, dataSearchableDocuments, lastShiftRangeIds, setMultiSelections]
  );

  const renderRow = useCallback(
    (searchableDoc: SearchableDocument, selected?: boolean) => (
      <DocuListRow
        mode={mode}
        menu={menu}
        showDate={showDate}
        loading={isLoading}
        data={searchableDoc}
        selected={!!selected}
        appearance={appearance}
        data-testid="docu-list-item"
        multiselectOn={multiSelectionOn}
        showDescription={showDescription}
        active={!!activeDocumentId && searchableDoc?.id === activeDocumentId}
      />
    ),
    [mode, isLoading, menu, activeDocumentId, multiSelectionOn, showDate, showDescription, appearance]
  );

  const renderPlaceholder = useCallback(
    () => (
      <DocuListRow
        loading
        mode={mode}
        menu={menu}
        selected={false}
        showDate={showDate}
        appearance={appearance}
        data-testid="docu-list-item"
        multiselectOn={multiSelectionOn}
        showDescription={showDescription}
      />
    ),
    [mode, menu, multiSelectionOn, showDate, showDescription, appearance]
  );

  const rowProps = useCallback(
    (searchableDoc: SearchableDocument) => ({
      onClick: (event: MouseEvent) => {
        const isShift = event.shiftKey;
        const isCmd = event.metaKey || event.ctrlKey;

        if (isShift) {
          return handleShiftClick(searchableDoc);
        }

        if (isCmd || multiSelectionOn) {
          setLastShiftRangeIds([]);
          const currentSelections = multiSelections ?? [];
          const nextSelections = toggleObject(currentSelections, searchableDoc);
          const isSelectedAfterToggle = nextSelections.some((d) => d.id === searchableDoc.id);

          setMultiSelections(nextSelections.length ? nextSelections : null);

          return setAnchor((prevAnchor) => {
            if (isSelectedAfterToggle) {
              return searchableDoc.id;
            }

            if (prevAnchor === searchableDoc.id) {
              return null;
            }

            return prevAnchor;
          });
        }

        openDocument(searchableDoc);
        setAnchor(null);
        setLastShiftRangeIds([]);
      },
      onDoubleClick: () => {
        if (!multiSelectionOn) {
          onDoubleClick?.(searchableDoc);
        }
      },
    }),
    [handleShiftClick, multiSelectionOn, multiSelections, onDoubleClick, openDocument, setMultiSelections]
  );

  return (
    <>
      <ul className={styles.docuControls} {...rest}>
        <li>
          <Search value={searchValue} appearance="filled" onChange={(e) => onSearchChange(e.target.value)} />
          <div className={styles.actions}>
            {hasFilters && (
              <Filters
                value={{
                  date,
                  excludedTagIds,
                  includedTagIds: tagIds,
                }}
                onChange={(next) => onAppliedFiltersChange({ ...next, date: next.date ?? null })}
              />
            )}
            <SortMenu
              iconSize={20}
              onSort={isSearching ? setSearchSort : onSort}
              showName={false}
              showArrow={false}
              sort={menuSort!}
              showRelevanceSort={isSearching}
              showNumTagsSort={mode === 'tag'}
            />
            {actions}
          </div>
        </li>
        {!!filters && (
          <li className={styles.filtersHolder} data-testid="doculist-filters-holder">
            {filters}
          </li>
        )}
      </ul>
      {listHeader}
      <AnimatePresence>
        {!isLoading && !data.length ? (
          noData
        ) : (
          <>
            <SmartList<SearchableDocument>
              overscan={30}
              resetKey={resetKey}
              rowProps={rowProps}
              loading={isLoading}
              rowRenderer={renderRow}
              placeholderRenderer={renderPlaceholder}
              defaultSize={defaultSize}
              activeId={activeDocumentId}
              data={dataSearchableDocuments}
              multiSelectionOn={multiSelectionOn}
              paddingEnd={bottomActions ? 94 : 20}
              onLastItemVisible={setIsLastItemVisible}
              selections={multiSelectionOn ? multiSelections : selections}
            />
            <MiniActionBar
              on={multiSelectionOn}
              onClear={clearSelections}
              selections={multiSelections || []}
              {...conditionalSpread(
                {
                  onManageTags: () => {},
                },
                mode !== 'tag'
              )}
            />
            <AnimatePresence initial={false}>
              {!isLoading && !multiSelectionOn && !!bottomActions && (
                <motion.div
                  key="bottom-actions"
                  data-testid="bottom-actions"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className={clsx(styles.docuBottomActions, {
                    [styles.lastItemVisible]: isLastItemVisible,
                  })}
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                  }}
                >
                  {bottomActions}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

interface DocuListProps {
  className?: string;
  noData?: ReactNode;
  showDate?: boolean;
  searchValue: string;
  actions?: ReactNode;
  filters?: ReactNode;
  listHeader?: ReactNode;
  defaultSize?: number;
  hasFilters?: boolean;
  'data-testid'?: string;
  multiselectOn?: boolean;
  searchableTypes: string[];
  mode?: 'document' | 'tag';
  showDescription?: boolean;
  resetFilters?: () => void;
  activeDocumentId?: string;
  bottomActions?: ReactNode;
  selections: DocumentWithId[];
  searchQuery?: Partial<SearchQuery>;
  appearance?: 'default' | 'greyscale';
  appliedFilters: SidebarAppliedFilters;
  onSearchChange: (value: string) => void;
  onDoubleClick?: (item: SearchableDocument) => void;
  menu: (data: SearchableDocument) => MenuProps['data'];
  onAppliedFiltersChange: (value: SidebarAppliedFilters) => void;
}

const DocuListRow = (props: ListItemProps) => {
  const { data } = props;
  const { activityState, editorState } = useAgentRunUiState(data?.id);
  return (
    <ListItem
      {...props}
      agentRunActivityState={activityState}
      agentRunEditorState={data?.hasPendingReview ? 'review' : editorState}
    />
  );
};
